import { Component, HostListener, OnInit, effect, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Company } from 'src/app/models/company.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ClockRegisterService } from 'src/app/services/clock-register.service';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { AddNewWorkingTimeDialogComponent } from './employees/activity/working-times-history/add-new-working-time-dialog/add-new-working-time-dialog.component';
import { WorkingTime } from 'src/app/models/working_time.model';
import { GeneralSearchDialogComponent } from '../utils/general-search-dialog/general-search-dialog.component';
import { PermissionsService } from 'src/app/services/permissions.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { environment } from 'src/environments/environment';
import { TopMenuClientComponent } from './clients/top-menu-client/top-menu-client.component';
import { TopMenuEmployeeComponent } from './employees/top-menu-employee/top-menu-employee.component';
import { TopMenuPartnerComponent } from './partners/top-menu-partner/top-menu-partner.component';
import { LateralMenuEmployeeComponent } from './employees/lateral-menu-employee/lateral-menu-employee.component';
import { LateralMenuClientComponent } from './clients/lateral-menu-client/lateral-menu-client.component';
import { LateralMenuPartnerComponent } from './partners/lateral-menu-partner/lateral-menu-partner.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { PlatformService } from 'src/app/services/platform.service';
import { UpdateAvailableDialogComponent } from './shared/update-available-dialog/update-available-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TopMenuClientComponent,
    TopMenuEmployeeComponent,
    TopMenuPartnerComponent,
    LateralMenuEmployeeComponent,
    LateralMenuClientComponent,
    LateralMenuPartnerComponent,
    MatButton,
    MatIcon,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class MainComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);
  private auth = inject(AuthService);
  clock = inject(ClockRegisterService);
  private customerService = inject(CustomerService);
  private api = inject(ApiService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private permissions = inject(PermissionsService);
  employee = inject(EmployeeService);
  platform = inject(PlatformService);
  private snackBar = inject(MatSnackBar);

  clientSelectControl = new UntypedFormControl();
  customerSelected: Company = null as any;
  available_customers: Company[] = [];
  showHeaderMenu: boolean = false;
  me: User = null as any;
  minimized: boolean;
  version: string;
  private isWorkingTimeDialogOpen: boolean = false;

  constructor() {
    this.version = environment.version;

    // Escuchar cambios en el signal de nueva versión usando effect
    // effect() debe estar en el constructor (contexto de inyección)
    if (this.platform.isElectronApp()) {
      effect(() => {
        const version = this.platform.newVersionAvailable();
        if (version) {
          // Llamar de forma asíncrona para evitar problemas con el contexto
          this.showVersionUpdateNotification(version).catch(() => {
            // Ignorar errores silenciosamente
          });
        }
      });
    }
  }

  ngOnInit(): void {
    this.minimized = Boolean(localStorage.getItem('isMainSidebarMinimized') === 'true');
    this.checkStartAttribute();
    this.listenCustomerSelected();
    this.listenControlSearch();
    this.me = this.userService.getCurrentUser();
    this.redirectByRoleOnRootRoute();
    this._listenUser();
    this.listenForVersionUpdates();
    this.initVersionUpdatesSubscription();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const key = event.key;
    const wasWriting = ['input', 'textarea'].indexOf((event.target as any).tagName.toLowerCase()) >= 0;
    const isControlOrCmdPressed = event.ctrlKey || event.metaKey;
    const isShiftAndCmdPressed = event.shiftKey && event.metaKey;

    if (key === '+' && !isControlOrCmdPressed && !wasWriting && this.permissions.isWorker()) {
      this.addWorkingTime();
    } else if (((key === 'k' && isControlOrCmdPressed) || key === '¡') && !wasWriting && this.permissions.isWorker()) {
      this.openGeneralSearch();
    } else if (key === ' ' && isShiftAndCmdPressed && !wasWriting) {
      this.toggleSidebar();
    }
  }

  addWorkingTime() {
    if (this.isWorkingTimeDialogOpen) {
      return;
    }

    this.isWorkingTimeDialogOpen = true;

    const dialogRef = this.dialog.open(AddNewWorkingTimeDialogComponent, {
      width: '650px',
      data: {},
    });

    dialogRef
      .afterClosed()
      .toPromise()
      .then((data: WorkingTime) => {
        this.isWorkingTimeDialogOpen = false;
        if (data != null) {
          this.api
            .createWorkingTime(data)
            .toPromise()
            .then(() => {
              this.employee.sendUpdateHoursTopMenu();
            })
            .catch((err: { error?: { error?: string } }) => {
              this.snackBar.open(err?.error?.error ?? 'Error al registrar el tiempo.', undefined, { duration: 6000 });
            });
        }
      });
  }

  openGeneralSearch() {
    this.dialog.open(GeneralSearchDialogComponent, {
      width: '650px',
      data: {},
      panelClass: 'custom-dialog-container',
    });
  }

  clickClock() {
    this.clock.click()?.subscribe();
  }

  redirectByRoleOnRootRoute() {
    if (this.router.url === '/') {
      if (this.me.role.slug == 'worker') {
        this.router.navigate(['/employees']);
      } else if (this.me.role.slug == 'partner') {
        this.router.navigate(['/partners/company-products']);
      } else if (this.me.role.slug == 'client') {
        this.router.navigate(['/clients']);
      } else {
        this.router.navigate(['/clients']);
      }
    }
  }

  selectCustomer(c: Company) {
    this.customerService.selectCustomer(c);
  }

  resetCustomer() {
    this.available_customers = [];
    this.clientSelectControl.setValue('');
    this.customerService.reset();
  }

  displayFn(company: Company): string {
    return company ? company.fact_name : '';
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/auth', 'login']);
  }

  toggleSidebar(): void {
    this.minimized = !this.minimized;
    localStorage.setItem('isMainSidebarMinimized', this.minimized.toString());
  }

  openUpdateDialog(version: string) {
    this.dialog.open(UpdateAvailableDialogComponent, {
      width: '500px',
      data: { version },
      disableClose: true,
    });
  }

  private _listenUser() {
    this.userService.currentUser.subscribe(data => {
      this.me = data;
    });
  }

  private checkStartAttribute() {
    const start_attribute = this.activatedRoute.snapshot.queryParamMap.get('start');

    if (start_attribute != null) {
      this.router.navigate(this.activatedRoute.snapshot.url, {
        queryParams: {},
      });
    }
  }

  private listenCustomerSelected() {
    this.customerService.customerObservable.subscribe(data => {
      this.customerSelected = data;
    });
  }

  private listenControlSearch() {
    this.clientSelectControl.valueChanges.subscribe((search_string: string) => {
      if (search_string != null && search_string.length >= 3) {
        const params = {
          search: search_string,
        };
        this.api.getCompanies(params).subscribe(data => {
          this.available_customers = data.data;
        });
      } else this.available_customers = [];
    });
  }

  private listenForVersionUpdates() {
    if (!this.platform.isElectronApp()) {
      return;
    }

    // Escuchar cambios en el signal de nueva versión usando effect
    // effect() debe estar en un contexto de inyección, por lo que lo ejecutamos en el constructor
    // pero solo si es Electron
  }

  private async showVersionUpdateNotification(version: string) {
    // No mostrar popup si el modo reunión está activado
    const isMeetingMode = await firstValueFrom(this.employee.meetingModeEnabled);
    if (isMeetingMode) {
      return;
    }

    // Mostrar snackbar
    const snackBarRef = this.snackBar.open(`Nueva versión disponible: ${version}`, 'Instalar', {
      duration: 10000,
      panelClass: ['update-available-snackbar'],
    });

    snackBarRef.onAction().subscribe(() => {
      this.openUpdateDialog(version);
    });

    // Mostrar diálogo después de un breve delay
    setTimeout(() => {
      this.openUpdateDialog(version);
    }, 2000);
  }

  private initVersionUpdatesSubscription() {
    // Suscribirse después de que el usuario esté autenticado y PusherService esté inicializado
    // Esperar un poco para asegurar que PusherService.initUser() ya se haya ejecutado
    setTimeout(() => {
      if (this.platform.isElectronApp() && this.me) {
        this.platform.subscribeToVersionUpdates();
      }
    }, 1000);
  }
}
